import { ampli } from '@/ampli';
import { MouseEventHandler, useEffect, useState } from 'react';
import { useMedia } from 'react-use';

import { NETWORK_NAME_PER_CHAIN_ID } from '@/constants/chain';

import { breakpoints } from '@/styles/util';

import { CheckCircleIcon } from '@/ui/Icons/CheckCircleIcon';
import { ExternalLinkIcon } from '@/ui/Icons/ExternalLinkIcon';
import { ExternalLink } from '@/ui/Link/ExternalLink';
import {
  MatchaTooltip,
  MatchaTooltipContent,
  MatchaTooltipPopover,
  MatchaTooltipTrigger,
} from '@/ui/MatchaTooltip';

import { classNames } from '@/utils/classnames';

import { TokenResult } from '../../../utils/0x/token-registry.types';
import { formatShortAddress } from '../../../utils/address';

const TWO_SECONDS = 2000;

interface Props {
  token: TokenResult | undefined;
  className?: string;
  explorerHref?: string;
}
export const TokenProfileContractAddress = ({ token, className, explorerHref }: Props) => {
  const [isCopied, setIsCopied] = useState(false);
  const [showCopyAddressTooltip, setShowCopyAddressTooltip] = useState(false);
  const isMobile = useMedia(breakpoints.mobile, false);

  useEffect(() => {
    let timeout: NodeJS.Timeout | null = null;
    if (isCopied) {
      timeout = setTimeout(() => {
        setShowCopyAddressTooltip(false);
        setTimeout(() => {
          setIsCopied(false);
        }, 100);
      }, TWO_SECONDS);
    }

    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [isCopied]);

  const handleCopyAddress: MouseEventHandler<HTMLButtonElement> = async (e) => {
    if (!token) return;

    e.preventDefault();
    ampli.tokenAddressCopied({
      token_address: token.address,
      token_chain_id: token.chainId,
      token_chain_name: NETWORK_NAME_PER_CHAIN_ID[token.chainId] || '',
    });
    await navigator.clipboard.writeText(token.address);
    setIsCopied(true);
    if (!isMobile) setShowCopyAddressTooltip(true);
  };

  return (
    <div className={classNames('flex items-center', className)}>
      {token ? (
        <MatchaTooltip open={showCopyAddressTooltip ? 'desktop' : false}>
          <MatchaTooltipTrigger>
            <button
              className="px-2 transition-colors tablet:hover:text-token-gray-900"
              onClick={handleCopyAddress}
              onMouseOver={() => !isMobile && setShowCopyAddressTooltip(true)}
              onMouseOut={() => !isCopied && setShowCopyAddressTooltip(false)}
            >
              <span className="pointer-events-none">{formatShortAddress(token.address, 6, 4)}</span>
            </button>
          </MatchaTooltipTrigger>
          <MatchaTooltipContent>{isCopied ? 'Copied' : 'Copy address'}</MatchaTooltipContent>
          <MatchaTooltipPopover sideOffset={9} />
        </MatchaTooltip>
      ) : undefined}
      {token && explorerHref && (
        <ExternalLink
          className="-ml-1.5 flex shrink-0 items-center rounded-sm text-token-gray-700 transition-colors focus-visible:outline-token-gray-500 tablet:hover:text-token-gray-900"
          href={explorerHref}
        >
          <span className="sr-only">{`View ${token.symbol} on block explorer`}</span>
          {/* The tooltip never opens on mobile, so the icon swap is the tap's only feedback. */}
          {isMobile && isCopied ? (
            <CheckCircleIcon aria-hidden className="ml-0.5 size-3.5" />
          ) : (
            <ExternalLinkIcon className="ml-0.5 size-3.5" />
          )}
        </ExternalLink>
      )}
    </div>
  );
};
